/**
 * figures.js — how a figure is spelled on Deal Reports
 * ====================================================
 * Usability review of 23 October 2026, item 6: one style across the app,
 * negatives in parentheses. Every spelling here delegates to
 * `components/format.js`; what is left is the page's own vocabulary — which
 * currency a deal reads in, and the three precisions it uses:
 *
 *   dealMoney     whole units      a deal's headline figures
 *   exactMoney    to the cent      the P&L bridge and anything a reader adds up
 *   compactFigure 9.2M, (3.5M)     the deal list, where the currency has its own column
 *   signedMoney   +SEK 44,784.56   the activity feed, where direction is the point
 *
 * A missing figure is "n/a" or a dash, never 0.00 (CLAUDE.md 0.30).
 */
import { compactMoney, money, toNumber, DASH } from "../../components/format";

export { day, DASH } from "../../components/format";

/** A headline figure in whole units of its currency. */
export const dealMoney = (value, currency = "USD") => money(value, { currency, whole: true });

/** To the cent, so the lines of a statement add down to its total. */
export const exactMoney = (value, currency = "USD") =>
  toNumber(value) === null ? "n/a" : money(value, { currency });

/** 9.2M, 640K, (3.5M) — the currency is in its own column beside it. */
export function compactFigure(value) {
  if (toNumber(value) === null) return DASH;
  return compactMoney(value, { currency: "USD" }).replace("$", "");
}

/** Money in or out, with its direction: "+SEK 44,784.56", "($316,172.28)". */
export function signedMoney(value, currency = "USD") {
  const n = toNumber(value);
  if (n === null) return "";
  const text = money(n, { currency: currency || "USD" });
  return n > 0 && text !== money(0, { currency: currency || "USD" }) ? `+${text}` : text;
}

/** A share count or a price, grouped, no currency. */
export const plainNumber = (value, digits = 2) => {
  const n = toNumber(value);
  if (n === null) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const shares = (value) => {
  const n = toNumber(value);
  return n === null ? DASH : Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
};

// A deal's own currency, or the list of them. A mixed deal is never "USD"
// here: its face-value figures are a sum of currencies and are labelled as one.
export const currencyOf = (deal) => {
  const list = deal?.currency || [];
  if (list.length === 1) return list[0];
  if (list.length > 1) return list.join(" + ");
  return "USD";
};

// What to actually show for a deal, and in what currency.
//
// A single-currency deal reads in its own currency: krona for a Nasdaq
// Stockholm position, because that is what its documents and its broker
// statements are written in.
//
// A mixed deal has no such number. Helena's Hilbert Group note went out in
// dollars and its conversion shares are sold in krona, so the deal's own
// `summary` adds the two at face value and means nothing. Where the API
// converted it — every flow at the Riksbank fixing for its own date — the deal
// is shown in dollars instead. Where the conversion failed for want of a rate,
// the face values are all there is, and the page says so in the header and
// beside every figure rather than labelling the sum with one of its
// currencies: on 14 September 2026 a header read "Cumulative Cash · USD" over
// SEK 21.5m of proceeds added to USD -7.6m of funding.
export const viewOf = (deal) => {
  if (!deal) return { currency: "USD", summary: null, converted: false, unconverted: false };
  if (deal.currency_is_mixed && deal.summary_usd) {
    return {
      currency: "USD",
      summary: { ...deal.summary, ...deal.summary_usd },
      converted: true,
      unconverted: false,
    };
  }
  return {
    currency: currencyOf(deal),
    summary: deal.summary,
    converted: false,
    unconverted: Boolean(deal.currency_is_mixed),
  };
};

/** The company a deal is with: the issuer on its security, else the counterparty. */
export const companyOf = (record) => (record?.issuer || record?.counterparty || "").trim();

/**
 * The line under a deal's name: who it is with and the ticker, when either
 * says something the name does not. A counterparty can be a ticker ("MI" on
 * NFT Limited's deal, CLAUDE.md 0.2), so the issuer is read first.
 */
export function dealSubline(record) {
  const name = (record?.name || "").toLowerCase();
  const company = companyOf(record);
  const ticker = (record?.ticker || "").trim();
  const bits = [];
  if (company && company.toLowerCase() !== name) bits.push(company);
  if (ticker && !name.includes(ticker.toLowerCase()) && ticker.toLowerCase() !== company.toLowerCase()) {
    bits.push(ticker);
  }
  return bits.join(" · ");
}
